import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginAsync, signInWitGoogleAsync } from "./redux-toolkit/store/reducers/auth.reducer";
import AuthIllustration from "assets/img/auth/auth.webp";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { MdEmail, MdLock } from "react-icons/md";
import { FcGoogle } from "react-icons/fc";

export function Login({ onLogin }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);

  // Check if already authenticated
  useEffect(() => {
    // Check all possible token sources
    const token = 
      sessionStorage.getItem('token') || 
      authState?.user?.token || 
      JSON.parse(localStorage.getItem('state'))?.auth?.user?.token;
    
    if (token) {
      console.log("Already authenticated, redirecting to dashboard");
      navigate("/dashboard");
    }
  }, [navigate, authState]);

  // Force dark mode
  useEffect(() => {
    // Add dark mode class to html and body
    document.documentElement.classList.add('dark');
    document.body.classList.add('dark');
    
    // Optional: Remove light theme if present
    document.documentElement.classList.remove('light');
    document.body.classList.remove('light');
    
    return () => {
      // If you want to clean up when component unmounts (usually not needed)
      // document.documentElement.classList.remove('dark');
      // document.body.classList.remove('dark');
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
    
    // Clear login error when user makes changes
    if (loginError) {
      setLoginError(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.password) newErrors.password = "Password is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setLoginError(null);
    
    try {
      const response = await dispatch(loginAsync(formData)).unwrap();
      console.log("Login response:", response);
      
      // Store token in multiple locations for reliability
      if (response.token) {
        // 1. Store in session storage
        sessionStorage.setItem("token", response.token);
        
        // 2. Keep in localStorage as part of state too
        // (This may be redundant if store.js already handles this,
        // but better safe than sorry)
        try {
          const currentState = JSON.parse(localStorage.getItem('state') || '{}');
          const updatedState = {
            ...currentState,
            auth: {
              ...currentState.auth,
              isAuthenticated: true,
              user: response,
            }
          };
          localStorage.setItem('state', JSON.stringify(updatedState));
        } catch (err) {
          console.error("Error saving auth state to localStorage:", err);
        }
        
        console.log("Authentication successful! Token stored.");
        
        // Call the onLogin callback if provided
        if (typeof onLogin === "function") {
          onLogin();
        }
        
        // Redirect to dashboard
        navigate("/dashboard");
      } else {
        console.error("No token received in login response");
        setLoginError("Authentication failed - no token received");
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoginError(
        error?.message || "Authentication failed. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Sign In
  const handleGoogleSignIn = async () => {
    try {
      // Initialize Google Auth
      const auth2 = window.gapi.auth2.getAuthInstance();
      
      if (!auth2) {
        console.error("Google Auth is not initialized");
        setLoginError("Google authentication is not available at the moment");
        return;
      }
      
      // Start Google sign-in flow
      setLoading(true);
      const googleUser = await auth2.signIn();
      const idToken = googleUser.getAuthResponse().id_token;
      
      // Send Google token to our backend
      const response = await dispatch(signInWitGoogleAsync({ token: idToken })).unwrap();
      
      // Handle successful authentication
      if (response.token) {
        // Store token in session storage
        sessionStorage.setItem('token', response.token);
        
        // Update state in localStorage
        try {
          const currentState = JSON.parse(localStorage.getItem('state') || '{}');
          const updatedState = {
            ...currentState,
            auth: {
              ...currentState.auth,
              isAuthenticated: true,
              user: response,
            }
          };
          localStorage.setItem('state', JSON.stringify(updatedState));
        } catch (err) {
          console.error("Error saving auth state to localStorage:", err);
        }
        
        // Call onLogin callback
        if (typeof onLogin === "function") {
          onLogin();
        }
        
        // Navigate to dashboard
        navigate("/dashboard");
      }
    } catch (err) {
      console.error("Google sign-in error:", err);
      setLoginError("Failed to sign in with Google. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Load Google SDK
  useEffect(() => {
    // Load the Google SDK script
    const loadGoogleScript = () => {
      // Check if script is already loaded
      if (document.querySelector('script[src="https://apis.google.com/js/platform.js"]')) {
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/platform.js';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleAuth;
      document.head.appendChild(script);
    };

    // Initialize Google Auth after script loads
    const initializeGoogleAuth = () => {
      window.gapi.load('auth2', () => {
        window.gapi.auth2.init({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        }).then(() => {
          console.log("Google Auth initialized");
        }).catch(err => {
          console.error("Error initializing Google Auth:", err);
        });
      });
    };

    loadGoogleScript();
  }, []);

  return (
    <div className="relative float-right h-full min-h-screen w-full !bg-navy-900">
      <main className={`mx-auto min-h-screen`}>
        <div className="relative flex">
          <div className="mx-auto flex min-h-full w-full flex-col justify-start pt-12 md:max-w-[75%] lg:h-screen lg:max-w-[1013px] lg:px-8 lg:pt-0 xl:h-[100vh] xl:max-w-[1383px] xl:px-0 xl:pl-[70px]">
            <div className="mb-auto flex flex-col pl-5 pr-5 md:pl-12 md:pr-0 lg:max-w-[48%] lg:pl-0 xl:max-w-full">
              <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
                {/* Login form */}
                <div className="w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
                  <h4 className="mb-2.5 text-4xl font-bold text-white">
                    Sign In
                  </h4>
                  <p className="mb-9 ml-1 text-base text-gray-400">
                    Enter your email and password to sign in!
                  </p>

                  {loginError && (
                    <div className="mb-4 rounded-md bg-red-900/30 p-4 text-sm text-red-400">
                      {loginError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                      <label
                        htmlFor="email"
                        className="text-sm text-white"
                      >
                        Email*
                      </label>
                      <div className="relative mt-2 w-full">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          <MdEmail />
                        </span>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="mail@simmmple.com"
                          className={`block h-12 w-full rounded-xl border border-navy-700 bg-navy-900 pl-11 pr-4 text-sm font-medium text-white outline-none focus:border-blue-500 focus:ring-blue-500 ${
                            errors.email ? "border-red-500" : ""
                          }`}
                        />
                      </div>
                      {errors.email && (
                        <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                      )}
                    </div>

                    <div className="mb-6">
                      <label
                        htmlFor="password"
                        className="text-sm text-white"
                      >
                        Password*
                      </label>
                      <div className="relative mt-2 w-full">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          <MdLock />
                        </span>
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Min. 8 characters"
                          className={`block h-12 w-full rounded-xl border border-navy-700 bg-navy-900 pl-11 pr-12 text-sm font-medium text-white outline-none focus:border-blue-500 focus:ring-blue-500 ${
                            errors.password ? "border-red-500" : ""
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.password}
                        </p>
                      )}
                    </div>

                    <div className="mb-6 flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          id="remember-me"
                          name="remember-me"
                          type="checkbox"
                          className="h-4 w-4 rounded border-navy-700 bg-navy-900 text-blue-600 focus:ring-blue-500"
                        />
                        <label
                          htmlFor="remember-me"
                          className="ml-2 block text-sm text-white"
                        >
                          Keep me logged in
                        </label>
                      </div>
                      <Link
                        to="/forgot-password"
                        className="text-sm font-medium text-blue-400 hover:text-blue-300"
                      >
                        Forgot Password?
                      </Link>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="linear mt-2 w-full rounded-xl bg-blue-600 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50"
                    >
                      {loading ? "Signing In..." : "Sign In"}
                    </button>
                  </form>

                  <div className="mt-4 flex w-full flex-col items-center gap-4">
                    {/* 
                    <div className="mb-6 flex h-[50px] w-full items-center justify-center gap-2 rounded-xl bg-navy-800 hover:cursor-pointer hover:bg-navy-700 transition-all"
                         onClick={handleGoogleSignIn}>
                      <div className="rounded-full text-xl">
                        <FcGoogle />
                      </div>
                      <h5 className="text-sm font-medium text-white">
                        Sign In with Google
                      </h5>
                    </div>
                    */}

                    <span className="text-sm font-medium text-gray-400">
                      Not registered yet?{" "}
                      <Link
                        to="/signup"
                        className="text-sm font-medium text-blue-400 hover:text-blue-300"
                      >
                        Create an Account
                      </Link>
                    </span>
                  </div>
                </div>
              </div>
              <div className="absolute right-0 hidden h-full min-h-screen md:block lg:w-[49vw] 2xl:w-[44vw]">
                <div
                  className="absolute flex h-full w-full items-end justify-center bg-cover bg-center lg:rounded-bl-[120px] xl:rounded-bl-[200px]"
                  style={{ backgroundImage: `url(${AuthIllustration})` }}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
